import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { BackButton, FullPageSpinner } from 'components'
import { Redirect, useHistory, useParams } from 'react-router-dom'
import { displayEin } from 'utils'
import {
  AgreementDetailsActions,
  AgreementDetailsModals,
  AgreementDetailsTabContent,
} from '../components'
import {
  Path,
  AroActionItems,
  BDActionItems,
  isGfBDAgreementsEnabled,
} from 'config/portal'
import {
  GrandfatheredUserValue,
  ServicesAgreementStatus,
  CombinedServiceAgreementStatus,
} from 'config/automatic-rollovers'
import { BDCombinedAgreementStatus } from 'config/benefit-distributions'
import { TOAST_ERROR_CONTACT_SUPPORT } from 'config/notifications'
import { flashErrorMessage } from 'redux-flash'
import * as actions from '../actions'
import { downloadServicesAgreementFile } from 'api'
import { TabBar } from 'lp-components'
import ServicesAgreementParticipants from './ServicesAgreementParticipants'
import { selectors as globalSelectors } from '../../reducer'
import { ProductAgreementType } from 'config/products'
import participantFieldMappings from '../components/participantFieldMapping'
import BenefitDistributionOrders, {
  benefitDistributionOrderFieldMappings,
} from './BenefitDistributionOrders'

const propTypes = {
  fetchServicesAgreementDetails: PropTypes.func.isRequired,
  servicesAgreement: Types.servicesAgreementShow,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  clearServicesAgreementDetails: PropTypes.func.isRequired,
  fetchAvailableInitialInvestments: PropTypes.func.isRequired,
  availableInitialInvestments: PropTypes.arrayOf(
    Types.availableInitialInvestment
  ),
  shouldTriggerServicesAgreementDetailsFetch: PropTypes.bool.isRequired,
  setShouldTriggerServicesAgreementDetailsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllServicesAgreementsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllBDPlanAgreementsFetch: PropTypes.func.isRequired,
  currentUser: Types.user.isRequired,
  agreementsType: PropTypes.string.isRequired,
}

const defaultProps = {
  servicesAgreement: null,
  availableInitialInvestments: null,
}

const PARTICIPANTS_INITIAL_FILTERS = {
  status: [],
}

function NoDetailsProvidedCard({ sectionName }) {
  return (
    <div className="card no-details">
      <h2 className="section-header">{sectionName} Details</h2>
      <p className="not-provided">Not provided</p>
    </div>
  )
}
NoDetailsProvidedCard.propTypes = {
  sectionName: PropTypes.string.isRequired,
}

function getSecondaryTabLabel(isBenefitDistributions) {
  return isBenefitDistributions ? 'My Distributions' : 'Participants'
}

const Tab = {
  AGREEMENT: 'agreement',
  PARTICIPANTS: 'participants',
}

function PlanAgreementDetails({
  fetchServicesAgreementDetails,
  servicesAgreement,
  flashErrorMessageHandler,
  clearServicesAgreementDetails,
  fetchAvailableInitialInvestments,
  availableInitialInvestments,
  shouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch,
  currentUser,
  agreementsType,
}) {
  const history = useHistory()
  const isGrandfatheredUser =
    currentUser?.grandfatheredUser === GrandfatheredUserValue &&
    isGfBDAgreementsEnabled()
  const [errorExists, setErrorExists] = useState(false)
  const [showDownloadNotAvailableModal, setShowDownloadNotAvailableModal] =
    useState(false)
  const [showResendModal, setShowResendModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const { servicesAgreementId } = useParams()
  const [currentTab, setCurrentTab] = useState(Tab.AGREEMENT)
  const [participantsFilters, setParticipantsFilters] = useState(
    PARTICIPANTS_INITIAL_FILTERS
  )
  const [participantsSearch, setParticipantsSearch] = useState('')

  const isBenefitDistributions = agreementsType === ProductAgreementType.BD
  useEffect(() => {
    return () => {
      clearServicesAgreementDetails()
      setShouldTriggerServicesAgreementDetailsFetch(true)
    }
  }, [])

  useEffect(() => {
    async function getInitialInvestments() {
      try {
        await fetchAvailableInitialInvestments()
      } catch {
        setErrorExists(true)
      }
    }
    if (!isBenefitDistributions && !availableInitialInvestments)
      getInitialInvestments()
  }, [agreementsType, availableInitialInvestments, isBenefitDistributions])

  useEffect(() => {
    async function getPlanAgreementDetails() {
      try {
        await fetchServicesAgreementDetails(servicesAgreementId)
        setShouldTriggerServicesAgreementDetailsFetch(false)
      } catch {
        setErrorExists(true)
      }
    }
    if (shouldTriggerServicesAgreementDetailsFetch) getPlanAgreementDetails()
  }, [shouldTriggerServicesAgreementDetailsFetch])

  const isLoading =
    (!servicesAgreement ||
      (!isBenefitDistributions && !availableInitialInvestments)) &&
    !errorExists

  if (isLoading) return <FullPageSpinner />
  if (errorExists) {
    flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
    return (
      <Redirect
        to={
          isBenefitDistributions
            ? Path.BD_SERVICES_AGREEMENTS
            : Path.SERVICES_AGREEMENTS
        }
      />
    )
  }

  const {
    name,
    portalAgreementStatus,
    createdDate,
    planEIN,
    planNumber,
    portalAgreementType,
    planType,
    planStatus,
    providerPlanId,
    planSponsorCompany,
    planSponsorContact,
    consultantCompany,
    consultantContact,
    TPACompany,
    TPAContact,
    recordKeeperCompany,
    recordKeeperContact,
    signingMethod,
  } = servicesAgreement

  const handleDownloadSelect = async () => {
    try {
      await downloadServicesAgreementFile(servicesAgreementId, name)
    } catch (e) {
      if (e.status === 422) setShowDownloadNotAvailableModal(true)
      else setErrorExists(true)
    }
  }

  const displayPlanEinValue = displayEin(planEIN)

  const agreementAudience = currentUser.accountAudience

  const isCompletedAgreement =
    portalAgreementStatus === ServicesAgreementStatus.COMPLETED ||
    portalAgreementStatus ===
      CombinedServiceAgreementStatus.COMBINED_COMPLETED ||
    portalAgreementStatus === BDCombinedAgreementStatus.COMBINED_COMPLETED

  const actionItems = [
    {
      externalLink: process.env.REACT_APP_ARO_FUNDING_INSTRUCTIONS_URL,
      text: isBenefitDistributions
        ? BDActionItems.VIEW_FUNDING_INSTRUCTIONS
        : AroActionItems.VIEW_FUNDING_INSTRUCTIONS,
    },
    {
      handleSelect: () => handleDownloadSelect(),
      text: isBenefitDistributions
        ? BDActionItems.DOWNLOAD_COMPLETED_AGREEMENT
        : 'Download completed agreement',
    },
  ]

  return (
    <div className="services-agreement-details-view">
      <div className="header-bar">
        <BackButton
          link={(location) => {
            return {
              pathname: isBenefitDistributions
                ? Path.BD_SERVICES_AGREEMENTS
                : Path.SERVICES_AGREEMENTS,
              search: location.state?.search,
            }
          }}
        >
          {isBenefitDistributions
            ? 'Back to all benefit distributions'
            : 'Back to all agreements'}
        </BackButton>
        <div className="header-bar-content">
          <div className="header-bar-page-title">
            <h1 className="page-title">{name}</h1>
          </div>
          <AgreementDetailsActions
            showSigningActions={!isBenefitDistributions || isGrandfatheredUser}
            portalAgreementStatus={portalAgreementStatus}
            signingMethod={signingMethod}
            actionItems={actionItems}
            actionMenuLabel={
              isBenefitDistributions
                ? 'View benefit distribution action items'
                : 'View automatic rollover action items'
            }
            onInitiate={() =>
              history.push(
                isBenefitDistributions
                  ? '/benefit-distributions/select-payment-method'
                  : '/automatic-rollovers/select-rollover-type'
              )
            }
            onUploadSignedAgreement={() => setShowUploadModal(true)}
            onResendDocusignEmail={() => setShowResendModal(true)}
            onDownloadAgreement={handleDownloadSelect}
            initiateLabel={
              isBenefitDistributions
                ? BDActionItems.INITIATE_DISTRIBUTION
                : AroActionItems.INITIATE_ROLLOVER
            }
            uploadSignedAgreementLabel={
              isBenefitDistributions
                ? BDActionItems.UPLOAD_SIGNED_AGREEMENT
                : AroActionItems.UPLOAD_SIGNED_AGREEMENT
            }
            resendDocusignEmailLabel={
              isBenefitDistributions
                ? BDActionItems.RESEND_DOCUSIGN_EMAIL
                : AroActionItems.RESEND_DOCUSIGN_EMAIL
            }
            downloadAgreementLabel={
              isBenefitDistributions
                ? BDActionItems.DOWNLOAD_AGREEMENT
                : AroActionItems.DOWNLOAD_AGREEMENT
            }
          />
        </div>
      </div>
      {isCompletedAgreement && (
        <TabBar
          options={[
            { key: 'Agreement Details', value: Tab.AGREEMENT },
            {
              key: getSecondaryTabLabel(isBenefitDistributions),
              value: Tab.PARTICIPANTS,
            },
          ]}
          value={currentTab}
          onChange={setCurrentTab}
        />
      )}
      {currentTab === Tab.AGREEMENT && (
        <AgreementDetailsTabContent
          isLoading={shouldTriggerServicesAgreementDetailsFetch}
          name={name}
          portalAgreementStatus={portalAgreementStatus}
          createdDate={createdDate}
          planNumber={planNumber}
          providerPlanId={providerPlanId}
          portalAgreementType={portalAgreementType}
          planType={planType}
          planStatus={planStatus}
          agreementAudience={agreementAudience}
          planSponsorCompany={planSponsorCompany}
          planSponsorContact={planSponsorContact}
          displayPlanEinValue={displayPlanEinValue}
          TPACompany={TPACompany}
          TPAContact={TPAContact}
          consultantCompany={consultantCompany}
          consultantContact={consultantContact}
          recordKeeperCompany={recordKeeperCompany}
          recordKeeperContact={recordKeeperContact}
          NoDetailsProvidedCard={NoDetailsProvidedCard}
        />
      )}
      {currentTab === Tab.PARTICIPANTS &&
        (isBenefitDistributions ? (
          <BenefitDistributionOrders
            agreementStatus={portalAgreementStatus}
            servicesAgreementId={servicesAgreementId}
            baseFilters={PARTICIPANTS_INITIAL_FILTERS}
            isTabbedContent={true}
            filters={participantsFilters}
            handleFilter={(filters) => setParticipantsFilters(filters)}
            fieldMappings={benefitDistributionOrderFieldMappings}
          />
        ) : (
          <ServicesAgreementParticipants
            servicesAgreementId={servicesAgreementId}
            baseFilters={PARTICIPANTS_INITIAL_FILTERS}
            filters={participantsFilters}
            handleFilter={(filters) => setParticipantsFilters(filters)}
            search={participantsSearch}
            handleSearch={(q) => setParticipantsSearch(q)}
            fieldMappings={participantFieldMappings}
          />
        ))}
      <AgreementDetailsModals
        showUploadModal={showUploadModal}
        setShowUploadModal={setShowUploadModal}
        showDownloadNotAvailableModal={showDownloadNotAvailableModal}
        setShowDownloadNotAvailableModal={setShowDownloadNotAvailableModal}
        showResendModal={showResendModal}
        setShowResendModal={setShowResendModal}
        servicesAgreement={servicesAgreement}
        servicesAgreementId={servicesAgreementId}
        portalAgreementStatus={portalAgreementStatus}
        agreementsType={agreementsType}
        onUploadSuccess={() => {
          setShowUploadModal(false)
          setShouldTriggerServicesAgreementDetailsFetch(true)
          setShouldTriggerAllServicesAgreementsFetch(true)
          setShouldTriggerAllBDPlanAgreementsFetch(true)
        }}
      />
    </div>
  )
}

function mapStateToProps(state) {
  return {
    servicesAgreement: selectors.servicesAgreement(state),
    availableInitialInvestments: selectors.availableInitialInvestments(state),
    shouldTriggerServicesAgreementDetailsFetch:
      selectors.shouldTriggerServicesAgreementDetailsFetch(state),
    currentUser: globalSelectors.currentUser(state),
  }
}

const mapDispatchToProps = {
  fetchServicesAgreementDetails: apiActions.fetchServicesAgreementDetails,
  flashErrorMessageHandler: flashErrorMessage,
  clearServicesAgreementDetails: actions.clearServicesAgreementDetails,
  fetchAvailableInitialInvestments: apiActions.fetchAvailableInitialInvestments,
  setShouldTriggerServicesAgreementDetailsFetch:
    actions.setShouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerAllServicesAgreementsFetch:
    actions.setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch:
    actions.setShouldTriggerAllBDPlanAgreementsFetch,
}

PlanAgreementDetails.propTypes = propTypes
PlanAgreementDetails.defaultProps = defaultProps

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  PlanAgreementDetails
)
